import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { getChannel } from "@/mocks/channels";
import { formatDate, formatViews, toneSoft, toneText } from "@/utils/display";

type Variant = "grid" | "row" | "mini";

interface Props {
  article: Article;
  variant?: Variant;
  rank?: number;
}

export default function ArticleCard({ article, variant = "grid", rank }: Props) {
  const channel = getChannel(article.channel);
  const tone = channel?.tone ?? "primary";

  if (variant === "mini") {
    return (
      <article className="group flex items-start gap-3 border-b border-background-200 py-4 last:border-b-0">
        {typeof rank === "number" && (
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${toneSoft[tone]}`}
          >
            {rank}
          </span>
        )}
        <div className="min-w-0">
          <h4 className="text-sm font-medium leading-snug text-foreground-900">
            <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-700">
              {article.title}
            </Link>
          </h4>
          <p className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
          </p>
        </div>
      </article>
    );
  }

  if (variant === "row") {
    return (
      <article className="group grid grid-cols-1 gap-4 border-b border-background-200 py-6 last:border-b-0 sm:grid-cols-[220px_1fr]">
        <Link
          to={`/article/${article.slug}`}
          className="block h-[150px] w-full overflow-hidden rounded-lg bg-background-200 sm:h-[140px]"
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {channel && (
              <Link
                to={`/channel/${channel.slug}`}
                className={`rounded-md px-2 py-0.5 font-medium transition-opacity hover:opacity-80 ${toneSoft[tone]}`}
              >
                {channel.name}
              </Link>
            )}
            <span className="text-foreground-500">{formatDate(article.publishedAt)}</span>
          </div>
          <h3 className="mt-2 text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-700">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.excerpt}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {article.readMinutes} 分钟阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <Link to={`/article/${article.slug}`} className="block h-[190px] w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {channel && (
            <span className={`rounded-md px-2 py-0.5 font-medium ${toneSoft[tone]}`}>
              {channel.name}
            </span>
          )}
          <span className="text-foreground-500">{formatDate(article.publishedAt)}</span>
        </div>
        <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className={`flex items-center gap-1 font-medium ${toneText[tone]}`}>
            <i className="ri-arrow-right-line" aria-hidden="true" />
            阅读全文
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}